// Global offline status pill + pending-sales drawer + install button (P9.5). Rendered by
// OfflineProvider; only visible on the POS surface (/app/pos*). Additive — no edits to POS pages.
import { useEffect, useState } from "react";
import { Wifi, WifiOff, RefreshCw, CloudUpload, Download, AlertTriangle } from "lucide-react";
import { useOffline } from "@/context/OfflineContext";
import { allQueued } from "@/lib/offlineDb";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { AppTour } from "@/components/pos/AppTour";

function ago(iso) {
  if (!iso) return "never";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

export function OfflineStatusBar() {
  const ctx = useOffline();
  const [rows, setRows] = useState([]);
  // The provider is mounted outside the Router, so we read the path directly (no react-router hooks).
  const [path, setPath] = useState(typeof window !== "undefined" ? window.location.pathname : "");
  useEffect(() => {
    const update = () => setPath(window.location.pathname);
    window.addEventListener("popstate", update);
    const t = setInterval(update, 1000);
    return () => { window.removeEventListener("popstate", update); clearInterval(t); };
  }, []);

  const onPos = path.startsWith("/app/pos");
  useEffect(() => { if (onPos) allQueued().then(setRows); }, [onPos, ctx.pending]);
  if (!onPos) return null;

  const { online, pending, catalog, queue, install } = ctx;
  const syncing = catalog?.syncing || queue?.flushing;
  const conflicts = rows.filter((r) => r.status === "Conflict").length;

  return (
    <>
    <AppTour />
    <div className="fixed bottom-4 right-4 z-[60] flex items-center gap-2" data-testid="offline-status-bar">
      {install?.canInstall && (
        <Button size="sm" variant="outline" className="bg-background shadow" onClick={install.promptInstall} data-testid="pwa-install-btn">
          <Download className="h-3.5 w-3.5 mr-1.5" />Install POS
        </Button>
      )}
      <Sheet>
        <SheetTrigger asChild>
          <button
            data-testid="offline-status-pill"
            className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium shadow bg-background ${
              online ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}`}
          >
            {syncing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
            {syncing ? "Syncing…" : online ? "Online" : "Offline"}
            {pending > 0 && <Badge variant="secondary" data-testid="offline-pending-badge">{pending}</Badge>}
            {conflicts > 0 && <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />}
          </button>
        </SheetTrigger>
        <SheetContent data-testid="offline-drawer">
          <SheetHeader><SheetTitle>Offline sync</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span>Status</span>
              <span data-testid="offline-drawer-status" className={online ? "text-emerald-600" : "text-amber-600"}>{online ? "Online" : "Offline"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Cached catalog</span><span>{catalog?.count ?? 0} items · {ago(catalog?.lastSynced)}</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={!online || catalog?.syncing} onClick={() => catalog?.syncCatalog()} data-testid="sync-catalog-btn">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Sync catalog now
              </Button>
              <Button size="sm" disabled={!online || pending === 0 || queue?.flushing} onClick={() => queue?.flushNow()} data-testid="flush-queue-btn">
                <CloudUpload className="h-3.5 w-3.5 mr-1.5" />Sync {pending} sale(s)
              </Button>
            </div>
            {conflicts > 0 && (
              <Button size="sm" variant="destructive" onClick={() => window.location.assign("/app/offline-pos")} data-testid="offline-conflicts-link">
                <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />{conflicts} conflict(s) — resolve
              </Button>
            )}
            <div className="pt-2 border-t">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Pending offline sales</p>
              <div className="space-y-1.5 max-h-72 overflow-auto" data-testid="offline-pending-list">
                {rows.length === 0 && <p className="text-xs text-muted-foreground">No queued sales.</p>}
                {rows.map((r) => (
                  <div key={r.client_txn_id} className="flex items-center justify-between rounded border p-2 text-xs">
                    <span className="font-mono">{r.client_txn_id.slice(0, 8)}</span>
                    <span>₹{Number(r.payload?.total || 0).toFixed(2)}</span>
                    <Badge variant={r.status === "Conflict" ? "destructive" : r.status === "Synced" ? "default" : "secondary"}>{r.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
    </>
  );
}
